import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Plus, RefreshCw } from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { Avatar, Field, IdChip, ListCard, Modal, RowActions, Table, inputCls, rowCls, tdCls } from '../components/ui';

const Clients = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/clients');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((i) => (i.Libelle_client || '').toLowerCase().includes(q)) : data;
  }, [data, search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    try {
      await http.delete(`/api/clients/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/clients/${editingItem.idclient}`, formData);
      else await http.post('/api/clients', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Clients"
        subtitle="Gestion des clients"
        icon={Briefcase}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="p-6">
        <ListCard
          count={filtered.length} singular="client affiché" plural="clients affichés"
          search={search} onSearch={setSearch} placeholder="Rechercher un client"
          loading={loading} hasData={data.length > 0} icon={Briefcase}
          emptyTitle="Aucun client pour le moment" emptyText="Créez votre premier client pour commencer."
          onCreate={handleCreate} createLabel="Créer un client" createIcon={Plus}
        >
          <Table heads={['Client', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idclient} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar text={item.Libelle_client} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.Libelle_client}</p>
                      <p className="mt-0.5"><IdChip>#{item.idclient}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idclient)} />
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          title={editingItem ? 'Modifier le client' : 'Créer un client'}
          subtitle={editingItem ? editingItem.Libelle_client : 'Renseignez les informations du client'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="space-y-5 p-6">
            <Field label="Libellé">
              <input
                type="text"
                className={inputCls}
                value={formData.Libelle_client || ''}
                onChange={(e) => setFormData({ ...formData, Libelle_client: e.target.value })}
                required
              />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Clients;